// turnStream — the live-progress vocabulary a streaming `sendTurn` call
// emits into the host, PLUS the pure fold that applies it to a transcript.
//
// `httpPorts` relays this sum from the server's SSE stream (status/tool-step
// events only — see the header note on `httpPorts.sendTurn`). The fold also
// handles `text_delta` (token-by-token reply streaming), so a future
// real-token-streaming build (vendor-level streaming in `@bilda/anthropic`)
// only has to start emitting `text_delta` from the server side — no UI
// change, because the fold below already knows what to do with one.
//
// Pure: no I/O, no timers. `foldTurnStreamEvent` is a plain reducer over
// `ChatEntry[]`, unit-testable without a DOM or a network.

export type {
    TurnActionOfferEvent,
    TurnMaterialBuildEvent,
    TurnStatusEvent,
    TurnStreamEvent,
    TurnStreamIds,
    TurnStreamListener,
    TurnTextDeltaEvent,
    TurnToolCallEvent,
    TurnToolResultEvent,
};
export {
    dropPendingInformationRequests,
    foldTurnStreamEvent,
    sweepBuildingToStopped,
    transcriptHasCreatedMaterial,
};

import type {
    ActionOfferEntry,
    AgentActivityEntry,
    AgentActivityStep,
    AgentEntry,
    ChatEntry,
    InformationRequestEntry,
    InformationRequestPendingEntry,
    LiveActivity,
    MaterialBuildingEntry,
    ToolStepOutcome,
} from "../components/Chat/types.js";
import {
    foldBuild,
    initialBuild,
    type MaterialBuildDelta,
} from "./materialBuildModel.js";

// === The event sum ============================================================
// One variant per progress signal a turn can emit, in the order a turn
// naturally produces them: a status note (the agent's preamble before it
// calls tools), a tool call starting, that tool call's result, and —
// mock-only today — a slice of the reply's growing text. `id` on the first
// three is the stable key `foldTurnStreamEvent` uses to know WHICH step a
// `tool_result` closes (the same `toolCallId` `tool_call` carried).

type TurnStatusEvent = {
    readonly type: "status";
    readonly id: string;
    readonly text: string;
};
type TurnToolCallEvent = {
    readonly type: "tool_call";
    readonly id: string;
    readonly name: string;
};
type TurnToolResultEvent = {
    readonly type: "tool_result";
    readonly id: string;
    readonly outcome: ToolStepOutcome;
};
// A slice of a growing agent message. `messageId` is the app-minted identity
// of the message this delta belongs to (the SAME id the durable
// `agent_message_sent` / `agent_status_noted` will carry) — the fold routes by
// it, so two messages streaming into the same conversation (concurrent turns,
// or a tool-round preamble vs the final answer) land in SEPARATE entries and
// can never smear. `seq` is monotonic per message (0-based): the fold applies a
// delta only when its `seq` exceeds the entry's last-applied one, making
// re-delivery a no-op and out-of-order arrival non-scrambling.
type TurnTextDeltaEvent = {
    readonly type: "text_delta";
    readonly messageId: string;
    readonly seq: number;
    readonly delta: string;
};
// A slice of a streaming material BUILD (material-streaming-2026-09-01). Wraps
// the typed `MaterialBuildDelta` sum (its own identity — buildId + seq — carried
// inside), so the fold routes/orders by that identity exactly as the text-delta
// path does. A TRANSIENT side-effect-track signal, never a durable event: the
// durable truth is the final `material_created` marker (the settled MaterialCard).
type TurnMaterialBuildEvent = {
    readonly type: "material_build";
    readonly delta: MaterialBuildDelta;
};
// The transient turn-epilogue ActionOffer (LANE S3). Carries the already-parsed
// `ActionOfferEntry` (conversationAdapter narrowed the wire shape at the
// boundary). A TRANSIENT side-effect-track signal, never a durable event: it
// never reloads (an offer is a hint about the future, not a fact about the past).
type TurnActionOfferEvent = {
    readonly type: "action_offer";
    readonly offer: ActionOfferEntry;
};
// The transient elicitation FORM (LANE S3, DURABLE branch — F3). Carries the
// already-parsed `InformationRequestEntry` (conversationAdapter narrowed the wire
// shape at the boundary). Unlike `action_offer`, the form IS durable — this
// transient event only surfaces it LIVE mid-turn, keyed by the closing message's
// id so the reload-time durable projection dedups it to ONE entry.
type TurnInformationRequestEvent = {
    readonly type: "information_request";
    readonly request: InformationRequestEntry;
};
// The form-PENDING precursor (F53): the agent has begun emitting the
// `_request_information` block but its form JSON hasn't finished streaming. `id`
// is the round's message id — the SAME id the finished form (`information_request`
// above) carries, so the real form supersedes this placeholder to one entry.
type TurnInformationRequestPendingEvent = {
    readonly type: "information_request_pending";
    readonly id: string;
    // Progress steps written so far (D30); the newest event replaces the last.
    readonly parts: readonly string[];
};
// What the agent is doing right now (D51). Transient; it changes no transcript
// entry — the chat session keeps the latest one for the live row.
type TurnActivityEvent = {
    readonly type: "activity";
    readonly activity: LiveActivity;
};
type TurnStreamEvent =
    | TurnActivityEvent
    | TurnStatusEvent
    | TurnToolCallEvent
    | TurnToolResultEvent
    | TurnTextDeltaEvent
    | TurnMaterialBuildEvent
    | TurnActionOfferEvent
    | TurnInformationRequestEvent
    | TurnInformationRequestPendingEvent;

// A caller-supplied sink; `sendTurn` calls it zero or more times before its
// own promise resolves. Optional on the port — a caller that doesn't pass
// one gets exactly today's behaviour (one blocking call, no interim signal).
type TurnStreamListener = (event: TurnStreamEvent) => void;

// === The fold ==================================================================
// `ids` carry the one caller-minted id the fold still needs: `activityId`, the
// key of the turn's tool trail (minted before the turn so the fold stays
// a pure, total `(entries, event, ids) → entries` with no id-minting side
// effect). The streaming REPLY no longer rides a caller id — each `text_delta`
// carries its own app-minted `messageId`, so the reply entry is keyed by the
// message's real identity, not a per-turn placeholder. An id whose entry
// doesn't exist yet is created on that id's FIRST event; later events upsert.

type TurnStreamIds = {
    readonly activityId: string;
};

const foldTurnStreamEvent = (
    entries: readonly ChatEntry[],
    event: TurnStreamEvent,
    ids: TurnStreamIds,
): readonly ChatEntry[] => {
    switch (event.type) {
        case "status":
            // O-7: an agent status note carries the agent's OWN preamble prose
            // ("Läser kapitlet…"). It renders as a legible agent MESSAGE keyed
            // by the round's messageId — the same id the reload-time
            // `agent_status` projection uses (conversationAdapter), so the live
            // bubble and the reloaded entry are ONE entry (dedup-by-id handles
            // the handoff), never a dim demoted step. The note text is the
            // authoritative FULL preamble, so it supersedes any partially
            // streamed text that grew under this id. The ambient `agent_activity`
            // trail now carries TOOL mechanics only.
            return upsertAgentMessage(entries, event.id, event.text);
        case "tool_call":
            return withActivityStep(entries, ids.activityId, {
                type: "tool_running",
                id: event.id,
                name: event.name,
            });
        case "tool_result":
            return withToolResult(
                entries,
                ids.activityId,
                event.id,
                event.outcome,
            );
        case "text_delta":
            return withTextDelta(
                entries,
                event.messageId,
                event.seq,
                event.delta,
            );
        case "material_build":
            return withBuildDelta(entries, event.delta);
        case "action_offer":
            return withActionOffer(entries, event.offer);
        case "information_request":
            return withInformationRequest(entries, event.request);
        case "information_request_pending":
            return withInformationRequestPending(
                entries,
                event.id,
                event.parts,
            );
        case "activity":
            return entries;
    }
};

// Upsert a form-PENDING placeholder keyed by its id (F53): a re-publish with
// more progress `parts` (D30) updates it IN PLACE; a new one is appended at the
// tail (the turn's incoming hand-off, in the slot the real form will take).
// Ignored once the real form for that id has arrived (the pending must never
// shadow the finished form — order-of-arrival safe). Pure and total.
const withInformationRequestPending = (
    entries: readonly ChatEntry[],
    id: string,
    parts: readonly string[],
): readonly ChatEntry[] => {
    if (entries.some((e) => e.type === "information_request" && e.id === id)) {
        return entries;
    }
    const pending: InformationRequestPendingEntry = {
        type: "information_request_pending",
        id,
        parts,
    };
    const isMine = (e: ChatEntry): boolean =>
        e.type === "information_request_pending" && e.id === id;
    return entries.some(isMine)
        ? entries.map((e) => (isMine(e) ? pending : e))
        : [...entries, pending];
};

// Drop every form-PENDING placeholder (F53). The finished form supersedes its own
// placeholder by id (see `withInformationRequest`); this sweeps any placeholder
// still standing when a turn TERMINATES without a form landing (a turn that
// failed/was stopped/lost its connection mid-form-generation), so a "preparing a
// form" state can never outlive its turn. Pure and total — no placeholder present
// returns the list unchanged.
const dropPendingInformationRequests = (
    entries: readonly ChatEntry[],
): readonly ChatEntry[] =>
    entries.filter((e) => e.type !== "information_request_pending");

// Upsert the elicitation form entry, keyed by its id (the closing agent message
// id). A re-published form (same id) replaces rather than duplicates, and the
// authoritative reload — whose durable projection keys the form by the SAME id —
// supersedes this live entry by id (replaceOrAppend / the in-flight merge), the
// same handoff the streamed reply makes to its durable message. Appended at the
// tail when new — the form IS the turn's hand-off. Pure and total.
const withInformationRequest = (
    entries: readonly ChatEntry[],
    request: InformationRequestEntry,
): readonly ChatEntry[] => {
    // F53: the finished form SUPERSEDES its own PENDING placeholder (same id) —
    // drop it first so the placeholder never lingers beside the real form.
    const withoutPending = entries.filter(
        (e) =>
            !(e.type === "information_request_pending" && e.id === request.id),
    );
    return withoutPending.some(
        (e) => e.type === "information_request" && e.id === request.id,
    )
        ? withoutPending.map((e) =>
              e.type === "information_request" && e.id === request.id
                  ? request
                  : e,
          )
        : [...withoutPending, request];
};

// Upsert the ActionOffer tail entry, keyed by its offer id, so a re-published
// offer (same id) replaces rather than duplicates and a new turn's offer is a
// distinct entry. Appended at the tail — it IS the turn's hand-off. Transient:
// gone on reload (conversationAdapter never projects it from the log). Pure.
const withActionOffer = (
    entries: readonly ChatEntry[],
    offer: ActionOfferEntry,
): readonly ChatEntry[] =>
    entries.some((e) => e.type === "action_offer" && e.id === offer.id)
        ? entries.map((e) =>
              e.type === "action_offer" && e.id === offer.id ? offer : e,
          )
        : [...entries, offer];

// Turn-end backstop for A-14 (invariant I3 — no card outlives its turn). The
// app layer emits a `build_stopped` delta on EVERY sendTurn exit (the primary
// guarantee), but a transport loss — a dead SSE stream on a connection failure
// — can drop that delta, leaving a transient card spinning with no terminal
// signal ever arriving. When a turn terminates in the shell (reply landed,
// failed, connection lost, or stopped), the terminal fold runs this over the
// transcript: every still-non-terminal (`planning` / `building`) material card
// is flipped to `stopped`, its partial and elapsed frozen. Pure and total — a
// card already `settled`/`stopped`, or a transcript with no build cards, is
// returned unchanged (so it is safe to run on the happy path too, where the
// build's own `build_settled` has usually already settled the card).
const sweepBuildingToStopped = (
    entries: readonly ChatEntry[],
): readonly ChatEntry[] =>
    entries.map((e) =>
        e.type === "material_building" &&
        (e.build.status === "planning" || e.build.status === "building")
            ? { ...e, build: { ...e.build, status: "stopped" } }
            : e,
    );

// G3-interim-B1 — does this transcript show a material the last turn (very
// likely) CREATED? True when a build reached a terminal state (a
// `material_building` swept to `stopped` or transient-`settled`) OR a durable
// `material` pointer is already present. Per-step commit means a build that got
// this far has almost certainly committed its `material_created` durably
// (.claude/analysis/g3-atomic-generation-rootcause.md §3), so a failed/interrupted
// turn in this state must NOT arm a blind Retry (it would re-run the turn and
// double-create) — the composer demotes to the calm "created, review it" note.
// Pure and total.
const transcriptHasCreatedMaterial = (entries: readonly ChatEntry[]): boolean =>
    entries.some(
        (e) =>
            e.type === "material" ||
            (e.type === "material_building" &&
                (e.build.status === "stopped" || e.build.status === "settled")),
    );

// The transcript id a build's transient card lives under: derived from the
// `buildId` (the tool-use id) so the DURABLE `MaterialEntry` — which
// conversationAdapter keys `${callId}-material` with the same tool-use id —
// SUPERSEDES this card by id on the authoritative reload (replaceOrAppend / the
// in-flight merge), the same handoff the streamed reply makes to its final
// message. Pure.
const buildEntryId = (buildId: string): string => `${buildId}-material`;

// Apply one build delta to the transcript, upserting the material_building entry
// identified by the delta's `buildId`. Find-or-create + `foldBuild` gives the
// same identity-keyed guarantees as the text-delta path: interleave fenced by
// buildId (a delta for build A never touches build B's card), smear +
// double-delivery fenced by seq inside `foldBuild`. `elapsedMs` mirrors the
// folded state's stamped read. Pure and total.
const withBuildDelta = (
    entries: readonly ChatEntry[],
    delta: MaterialBuildDelta,
): readonly ChatEntry[] => {
    const id = buildEntryId(delta.buildId);
    const existing = entries.find(
        (e): e is MaterialBuildingEntry =>
            e.type === "material_building" && e.id === id,
    );
    const base = existing?.build ?? initialBuild(delta.buildId);
    const build = foldBuild(base, delta);
    const entry: MaterialBuildingEntry = {
        type: "material_building",
        id,
        build,
        elapsedMs: build.elapsedMs,
    };
    if (existing === undefined) return [...entries, entry];
    return entries.map((e) => (e.id === id ? entry : e));
};

// Append one step to the activity entry at `id`, creating the entry (empty
// steps) on the first call. A `tool_running` step is always APPENDED, never
// merged — each is its own moment in the trail (its later result resolves it
// in place via `withToolResult`).
const withActivityStep = (
    entries: readonly ChatEntry[],
    activityId: string,
    step: AgentActivityStep,
): readonly ChatEntry[] => {
    const existing = entries.find(
        (e): e is AgentActivityEntry =>
            e.type === "agent_activity" && e.id === activityId,
    );
    if (existing === undefined) {
        const created: AgentActivityEntry = {
            type: "agent_activity",
            id: activityId,
            steps: [step],
        };
        return [...entries, created];
    }
    return entries.map((e) =>
        e.id === activityId && e.type === "agent_activity"
            ? { ...e, steps: [...e.steps, step] }
            : e,
    );
};

// Resolve a `tool_running` step (matched by its `toolCallId`) into a
// `tool_done` step carrying the outcome. A result with no matching running
// step (a race, or a step the trail never saw) is dropped — there's
// nothing honest to update.
const withToolResult = (
    entries: readonly ChatEntry[],
    activityId: string,
    toolCallId: string,
    outcome: ToolStepOutcome,
): readonly ChatEntry[] =>
    entries.map((e) => {
        if (e.id !== activityId || e.type !== "agent_activity") return e;
        return {
            ...e,
            steps: e.steps.map((step) =>
                step.type === "tool_running" && step.id === toolCallId
                    ? {
                          type: "tool_done",
                          id: step.id,
                          name: step.name,
                          outcome,
                      }
                    : step,
            ),
        };
    });

// Append a text slice to the agent message identified by `messageId`, creating
// it (empty markdown, no suggestions yet) on the first delta. Routing by the
// message's OWN identity is what makes smear impossible: two messages streaming
// into one conversation each own their entry. `seq` gives idempotent, ordered
// application — a delta is applied only when it advances the entry's cursor
// (`seq` strictly greater than the last applied), so a re-delivered or
// out-of-order delta is dropped rather than double-appended or interleaved.
const withTextDelta = (
    entries: readonly ChatEntry[],
    messageId: string,
    seq: number,
    delta: string,
): readonly ChatEntry[] => {
    const existing = entries.find(
        (e): e is AgentEntry => e.type === "agent" && e.id === messageId,
    );
    if (existing === undefined) {
        const created: AgentEntry = {
            type: "agent",
            id: messageId,
            markdown: delta,
            streamSeq: seq,
        };
        return [...entries, created];
    }
    // Idempotency + ordering gate: only a delta that advances the cursor is
    // applied. `streamSeq` is always present on a bubble the fold created; the
    // `?? -1` is a total fallback for the impossible "durable entry fed a delta"
    // shape (first delta then still applies).
    if (seq <= (existing.streamSeq ?? -1)) return entries;
    return entries.map((e) =>
        e.id === messageId && e.type === "agent"
            ? { ...e, markdown: e.markdown + delta, streamSeq: seq }
            : e,
    );
};

// Replace-or-create the agent message at `id` with `markdown` as its full,
// authoritative body — a DURABLE bubble (no `streamSeq`), because a status note
// carries the round's complete preamble, superseding any partial streamed text
// that grew under the same id. Constructed field-by-field (no cast). Total:
// creates the entry when absent, replaces it in place when present.
const upsertAgentMessage = (
    entries: readonly ChatEntry[],
    id: string,
    markdown: string,
): readonly ChatEntry[] => {
    const message: AgentEntry = { type: "agent", id, markdown };
    return entries.some((e) => e.type === "agent" && e.id === id)
        ? entries.map((e) => (e.type === "agent" && e.id === id ? message : e))
        : [...entries, message];
};
